/**
 * 项目概览弹窗组件
 * 点击项目卡片后弹出，展示项目详细数据
 * 底部按钮固定显示，不随内容滚动
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, message, Form, Input, InputNumber, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { StatisticsService } from '../../services/statisticsService';
import { getBudgetDetail } from '../../services/budgetService';
import { ProjectContractService } from '../../services/projectContractService';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import api from '../../services/api';
import { CoordinationCostService } from '../../services/coordinationCostService';
import { CoordinationCostType } from '../../types';
import type { CostDetailType } from './CostDetailTypes';
import BudgetOverviewTower from './BudgetOverviewTower';
import AddRecordModal, { RecordType } from './AddRecordModal';
import ActionButtons from './ActionButtons';
import BatchAdditionModal from '../../modules/progress/components/BatchAdditionModal';
import RectificationModal from '../../modules/progress/components/RectificationModal';

const { TextArea } = Input;
type QuickAddType = 'subcontract' | 'coordination';

export interface ProjectOverviewModalProps {
  visible: boolean;
  project: ProjectOverviewData | null;
  onClose: () => void;
  onEditProject?: (project: ProjectOverviewData) => void;
  onExport?: (project: ProjectOverviewData) => void;
  /** 数据变更后回调，通知父组件刷新 Dashboard 等外部缓存 */
  onDataChange?: () => void;
}

interface CacheData {
  data: any;
  timestamp: number;
}

const CACHE_TTL = 60 * 1000;
const statsCache = new Map<string, CacheData>();


const clearProjectStatsCache = (projectId?: string) => {
  if (projectId) {
    statsCache.delete(projectId);
  } else {
    statsCache.clear();
  }
};

const ProjectOverviewModal: React.FC<ProjectOverviewModalProps> = ({
  visible,
  project,
  onClose,
  onEditProject,
  onExport,
  onDataChange,
}) => {
  const { isMobile, isTablet } = useResponsiveConfig();
  const [loading, setLoading] = useState(false);
  const [projectStats, setProjectStats] = useState<any>(null);
  const [budgetDetail, setBudgetDetail] = useState<any>(null);
  const [contractSummary, setContractSummary] = useState<any>(null);
  const [addRecordVisible, setAddRecordVisible] = useState(false);
  const [recordType, setRecordType] = useState<RecordType | null>(null);
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const [quickAddType, setQuickAddType] = useState<QuickAddType>('subcontract');
  const [constructionType, setConstructionType] = useState<string | undefined>(undefined);
  const constructionTypeRef = useRef<string | undefined>(undefined);
  const [additionDrawerVisible, setAdditionDrawerVisible] = useState(false);
  const [rectificationDrawerVisible, setRectificationDrawerVisible] = useState(false);
  const [additionSubcontractors, setAdditionSubcontractors] = useState<any[]>([]);
  const [quickAddForm] = Form.useForm();
  const loadingRef = useRef(false);

  const fetchProjectStats = async (projectId: string, forceRefresh = false) => {
    const ct = constructionTypeRef.current;
    if (!forceRefresh) {
      const cached = statsCache.get(projectId + (ct ? `::${ct}` : ''));
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setProjectStats(cached.data);
        return;
      }
    }

    if (loadingRef.current) return;
    loadingRef.current = true;

    setLoading(true);
    try {
      const [response, budgetResponse, contractResponse] = await Promise.all([
        StatisticsService.getProjectComprehensiveStats(projectId, { constructionType: ct }),
        getBudgetDetail(projectId),
        ProjectContractService.getProjectSummary(projectId),
      ]);
      if (response.success && response.data) {
        setProjectStats(response.data);
        statsCache.set(projectId + (ct ? `::${ct}` : ''), {
          data: response.data,
          timestamp: Date.now()
        });
      }
      if (budgetResponse.success && budgetResponse.data) {
        setBudgetDetail(budgetResponse.data);
      }
      if (contractResponse.success && contractResponse.data) {
        setContractSummary(contractResponse.data);
      }
    } catch (error) {
      console.error('获取项目统计数据失败:', error);
      message.error('获取项目统计数据失败');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (visible && project?.id) {
      fetchProjectStats(project.id);
      api.get('/progress/subcontractors', { params: { status: 'active' } })
        .then((res: any) => {
          if (res?.success && res.data) {
            const list = Array.isArray(res.data) ? res.data : res.data.list || [];
            setAdditionSubcontractors(list);
          }
        }).catch(console.error);
    }
    if (!visible) {
      setProjectStats(null);
      setBudgetDetail(null);
      setContractSummary(null);
      setAddRecordVisible(false);
      setRecordType(null);
      setQuickAddVisible(false);
      setConstructionType(undefined);
      constructionTypeRef.current = undefined;
    }
  }, [visible, project?.id, constructionType, quickAddForm]);

  const getModalWidth = () => {
    if (isMobile) return '100%';
    if (isTablet) return '95%';
    return 1100;
  };

  const openAddRecord = (type: RecordType) => {
    setRecordType(type);
    setAddRecordVisible(true);
  };

  const closeAddRecord = () => {
    setAddRecordVisible(false);
    setRecordType(null);
  };

  const openQuickAdd = (type: QuickAddType) => {
    setQuickAddType(type);
    setQuickAddVisible(true);
    quickAddForm.resetFields();
    quickAddForm.setFieldsValue({
      projectId: project?.id,
      costDate: dayjs(),
      status: 'active',
      costType: CoordinationCostType.MISC,
      paidAmount: 0,
    });
  };

  const closeQuickAdd = () => {
    setQuickAddVisible(false);
    quickAddForm.resetFields();
  };

  const handleAddSuccess = () => {
    if (!project?.id) return;
    clearProjectStatsCache(project.id);
    fetchProjectStats(project.id, true);
    onDataChange?.();
  };

  const handleQuickAddSubmit = async () => {
    if (!project?.id) return;

    try {
      const values = await quickAddForm.validateFields();

      if (quickAddType === 'subcontract') {
        await api.post('/subcontract-costs', {
          projectId: project.id,
          contractName: values.contractName,
          costDate: values.costDate.format('YYYY-MM-DD'),
          contractAmount: values.contractAmount,
          paidAmount: values.paidAmount || 0,
          status: values.status,
          description: values.description,
        });
        message.success('分包成本记录创建成功');
      } else {
        await CoordinationCostService.createCoordinationCost({
          projectId: project.id,
          costDate: values.costDate.format('YYYY-MM-DD'),
          costType: values.costType,
          amount: values.amount,
          handler: values.handler,
          description: values.description,
        });
        message.success('协调成本记录创建成功');
      }

      closeQuickAdd();
      handleAddSuccess();
    } catch (error) {
      message.error(quickAddType === 'subcontract' ? '分包成本记录创建失败' : '协调成本记录创建失败');
    }
  };

  const handleInlineAdd = (type: CostDetailType) => {
    if (type === 'worktime' || type === 'equipment' || type === 'material') {
      openAddRecord(type);
      return;
    }

    if (type === 'subcontract') {
      openQuickAdd('subcontract');
      return;
    }

    if (type === 'coordination') {
      openQuickAdd('coordination');
    }
  };

  const handleBudgetRefresh = () => {
    if (!project?.id) return;
    clearProjectStatsCache(project.id);
    fetchProjectStats(project.id, true);
    onDataChange?.();
  };

  if (!project) return null;

  // revenueTotal 从 revenueBreakdown 求和，确保与合同模块/增项模块的合并值一致
  // budgetDetail.revenueTotal 是 Project 原始字段，不含合同管理模块的汇总
  // budgetDetail.revenueBreakdown.contract 已包含 manualContract + contractSummary.totalAmount
  // budgetDetail.revenueBreakdown.changeOrder 已包含 manualChangeOrder + additionModuleTotal
  const mergedRevenueBreakdown = budgetDetail?.revenueBreakdown || {
    contract: 0, changeOrder: 0, claim: 0, settlement: 0, other: 0,
  };
  const mergedRevenueTotal = Object.values(mergedRevenueBreakdown).reduce(
    (sum: number, v: number) => sum + (v || 0), 0
  );

  // 优先使用 stats 中的 totalProjectCost，兜底使用各成本项求和（与 Dashboard 口径一致）
  const mergedActualCost = projectStats?.summary?.totalProjectCost
    || Object.values({
      workTime: projectStats?.workTime?.totalCost,
      material: projectStats?.material?.totalCost,
      equipment: projectStats?.equipment?.totalCost,
      subcontract: projectStats?.subcontract?.totalCost,
      coordination: projectStats?.coordination?.totalCost,
    }).reduce((a: number, b: number) => a + (b || 0), 0)
    || project.actualCost
    || 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mergedProject: any = Object.assign({}, project, {
    budget: budgetDetail?.budget ?? project.budget,
    budgetBreakdown: budgetDetail?.budgetBreakdown,
    revenueTotal: mergedRevenueTotal,
    revenueBreakdown: mergedRevenueBreakdown,
    /** 手动录入的增项收入 */
    manualChangeOrder: budgetDetail?.manualChangeOrder ?? 0,
    /** 增项管理模块全量汇总值（不含手动录入） */
    additionModuleTotal: budgetDetail?.additionModuleTotal ?? 0,
    /** 手动录入的合同收入 */
    manualContract: budgetDetail?.manualContract ?? 0,
    /** 合同管理模块全量汇总值（不含手动录入） */
    contractModuleTotal: budgetDetail?.contractModuleTotal ?? 0,
    budgetVersion: budgetDetail?.budgetVersion,
    budgetLocked: budgetDetail?.budgetLocked,
    budgetLockedAt: budgetDetail?.budgetLockedAt,
    budgetLockedBy: budgetDetail?.budgetLockedBy,
    actualCost: mergedActualCost,
  });

  return (
    <>
      <Modal
        title={<span style={{ color: '#10171d' }}>项目概览 · {project.name}</span>}
        open={visible}
        onCancel={onClose}
        width={getModalWidth()}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : { top: 24 }}
        styles={{
          body: {
            maxHeight: isMobile ? 'calc(100vh - 220px)' : 'calc(88vh - 72px)',
            overflow: 'auto',
            padding: isMobile ? '12px' : '24px',
          },
        }}
        footer={loading ? null : (
          <ActionButtons
            project={mergedProject}
            onEditProject={onEditProject}
            isMobile={isMobile}
            constructionType={constructionType}
          />
        )}
        destroyOnHidden
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" tip="加载项目数据中..." />
          </div>
        ) : (
          <BudgetOverviewTower
            project={mergedProject}
            stats={projectStats}
            contractSummary={contractSummary}
            constructionType={constructionType}
            onConstructionTypeChange={(type) => {
              constructionTypeRef.current = type;
              setConstructionType(type);
              fetchProjectStats(project!.id, true);
            }}
            onBudgetRefresh={handleBudgetRefresh}
            onEditProject={onEditProject ? () => onEditProject(mergedProject) : undefined}
            onAddRecord={handleInlineAdd}
            onAddAddition={() => setAdditionDrawerVisible(true)}
            onAddRectification={() => setRectificationDrawerVisible(true)}
            onSetupBudget={onEditProject ? () => onEditProject(mergedProject) : undefined}
          />
        )}
      </Modal>

      <AddRecordModal
        visible={addRecordVisible}
        recordType={recordType}
        projectId={project.id}
        projectName={project.name}
        onClose={closeAddRecord}
        onSuccess={handleAddSuccess}
        isMobile={isMobile}
      />

      <Modal
        title={quickAddType === 'subcontract' ? '新增分包成本' : '新增协调成本'}
        open={quickAddVisible}
        onCancel={closeQuickAdd}
        onOk={handleQuickAddSubmit}
        okText="保存"
        destroyOnHidden
        styles={{
          header: { background: 'rgba(232, 237, 240, 0.98)', borderBottom: '1px solid rgba(16, 27, 35, 0.08)' },
          body: { background: 'rgba(232, 237, 240, 0.98)' },
          footer: { background: 'rgba(232, 237, 240, 0.98)', borderTop: '1px solid rgba(16, 27, 35, 0.08)' }
        }}
      >
        <Form form={quickAddForm} layout="vertical">
          {quickAddType === 'subcontract' ? (
            <>
              <Form.Item name="contractName" label="合同名称" rules={[{ required: true, message: '请输入合同名称' }]}>
                <Input placeholder="请输入合同名称" />
              </Form.Item>
              <Form.Item name="costDate" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="contractAmount" label="合同金额" rules={[{ required: true, message: '请输入合同金额' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入合同金额" />
              </Form.Item>
              <Form.Item name="paidAmount" label="已付金额">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入已付金额" />
              </Form.Item>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select options={[{ label: '进行中', value: 'active' }, { label: '已完成', value: 'completed' }, { label: '已取消', value: 'cancelled' }]} />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <TextArea rows={3} placeholder="备注（可选）" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="costDate" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="costType" label="费用类型" rules={[{ required: true, message: '请选择费用类型' }]}>
                <Select options={CoordinationCostService.getCostTypeOptions()} />
              </Form.Item>
              <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入金额" />
              </Form.Item>
              <Form.Item name="handler" label="经办人">
                <Input placeholder="请输入经办人" />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <TextArea rows={3} placeholder="备注（可选）" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <BatchAdditionModal
        visible={additionDrawerVisible}
        defaultProjectId={project.id}
        subcontractors={additionSubcontractors}
        onClose={() => setAdditionDrawerVisible(false)}
        onSuccess={handleAddSuccess}
      />

      <RectificationModal
        visible={rectificationDrawerVisible}
        defaultProjectId={project.id}
        onClose={() => setRectificationDrawerVisible(false)}
        onSuccess={handleAddSuccess}
      />
    </>
  );
};

export default ProjectOverviewModal;
